/**
 * 会社の設定（issue #124・docs/01 15-1「通知・上限値などの設定変更」・20-3）。
 *
 * この画面は**見るだけ**です。まとまりごとに、いまの値と「既定値のまま／変えています」を並べ、
 * 変えるときは「変える」から、そのまとまりだけの画面へ進みます（docs/06 原則3「1画面1目的」）。
 *
 * **「まだ効きません」の設定は、そうと分かるように出します**（値の置き場だけを先に作ってあるもの。
 * 変えても、いまの動きは変わりません・docs/01 20-3）。効かない設定を、効くものと思って
 * 黙って保存させないためです。
 *
 * 拠点の一覧・役職のできることは、左の「拠点」「役職とできること」の画面で変えます。
 * AI先輩が答えない領域は「AI先輩」の「答えない領域」の画面で扱います。
 *
 * アプリ名が長く、ログインの認証番号の SMS が1通に収まらないときは、上にそうお知らせします
 * （2通分の料金になるため・issue #524。知らせるだけで、何も止めません）。
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../api/context.js';
import { loadFailure, type LoadFailure } from '../api/load-failure.js';
import { useSession } from '../auth/session-context.js';
import type { SettingItem, SettingsScreen } from '../api/types.js';
import { ErrorBlock, LoadingBlock } from '../components/StatusBlock.js';
import { Card } from '../components/ui/Card.js';
import { PageHeader } from '../components/ui/PageHeader.js';
import { ResultBanner } from '../components/ui/ResultBanner.js';
import { smsBodyExceedsSingle } from '../settings/sms-length.js';
import { settingValueText } from '../settings/values.js';

export function SettingsPage(): ReactNode {
  const api = useApi();
  const { profile } = useSession();
  const appName = profile?.branding.app_name ?? '';
  const [data, setData] = useState<SettingsScreen | null>(null);
  const [error, setError] = useState<LoadFailure | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    api
      .get<SettingsScreen>('/admin/settings')
      .then((loaded) => {
        if (!cancelled) setData(loaded);
      })
      .catch((caught: unknown) => {
        if (!cancelled) setError(loadFailure(caught));
      });
    return () => {
      cancelled = true;
    };
  }, [api, reloadKey]);

  return (
    <article>
      <PageHeader
        title="会社の設定"
        description="会社ごとの決まりです。変えるときは、まとまりごとの「変える」から進みます。"
      />
      {appName && smsBodyExceedsSingle(appName) ? (
        <ResultBanner
          tone="info"
          message={`アプリ名「${appName}」が長いため、ログインの認証番号の SMS が2通分になり、送る費用が増えます。`}
          nextStep="アプリ名を短くするときは、導入の担当にご相談ください。"
        />
      ) : null}
      {error ? <ErrorBlock failure={error} onRetry={() => setReloadKey((n) => n + 1)} /> : null}
      {!error && !data ? <LoadingBlock label="会社の設定を読み込んでいます…" /> : null}
      {data
        ? data.groups.map((group) => (
            <Card
              key={group.key}
              title={group.title}
              description={group.description}
              actions={
                <Link
                  className="ui-button ui-button-secondary ui-button-compact"
                  to={`/settings/${group.key}`}
                  aria-label={`${group.title}を変える`}
                >
                  変える
                </Link>
              }
            >
              <dl className="admin-facts">
                {group.settings.map((setting) => (
                  <SettingFact key={setting.key} setting={setting} />
                ))}
              </dl>
            </Card>
          ))
        : null}
    </article>
  );
}

function SettingFact({ setting }: { setting: SettingItem }): ReactNode {
  return (
    <>
      <dt>{setting.label}</dt>
      <dd>
        <span className="admin-setting-value">{settingValueText(setting, setting.value)}</span>
        <span className="admin-tags">
          {setting.is_default ? (
            <span className="admin-tag admin-tag-muted">既定値のまま</span>
          ) : (
            <span className="admin-tag admin-tag-neutral">変えています</span>
          )}
          {setting.in_effect ? null : <span className="admin-badge">まだ効きません</span>}
        </span>
        {setting.in_effect ? null : <small>{setting.effect_note}</small>}
      </dd>
    </>
  );
}
